import React, {useState, useEffect, useMemo} from "react";
import {Select, Input, Checkbox} from "antd";
import {useParams} from "react-router-dom";

import {generateYearOptions} from "../../composable/useDates";

import BlockGetInfoById from "../video-control/BlockGetInfoById";

import "./styles/film-main-info.scss";

const {TextArea} = Input;

interface Props {
    changeFilm: (option: string, value: string | number) => void;
    updateOption: (key: string, value: any) => void,
    updateAllOptions: (data: any) => void,

    film: any;
}

function extractUrlFromHtml(value: string): string {
    const trimmed = value.trim();

    if (trimmed.startsWith('<')) {
        const match = trimmed.match(/src=["']([^"']+)["']/i);
        if (match && match[1]) {
            return convertYoutubeUrl(match[1]);
        }
        return trimmed;
    }

    return convertYoutubeUrl(trimmed);
}

// Конвертирует youtu.be/ID и youtube.com/watch?v=ID в youtube.com/embed/ID
function convertYoutubeUrl(url: string): string {
    // Формат: https://youtu.be/VIDEO_ID?si=...
    const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
    if (shortMatch) {
        const videoId = shortMatch[1];
        return `https://www.youtube.com/embed/${videoId}`;
    }

    // Формат: https://www.youtube.com/watch?v=VIDEO_ID&...
    const watchMatch = url.match(/youtube\.com\/watch\?v=([^&]+)/);
    if (watchMatch) {
        const videoId = watchMatch[1];
        return `https://www.youtube.com/embed/${videoId}`;
    }

    // Формат: https://www.youtube.com/embed/VIDEO_ID?si=...
    const embedMatch = url.match(/youtube\.com\/embed\/([^?&]+)/);
    if (embedMatch) {
        const videoId = embedMatch[1];
        return `https://www.youtube.com/embed/${videoId}`;
    }

    return url;
}

function FilmMainInfo({changeFilm, updateOption, updateAllOptions, film}: Props) {
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [year, setYear] = useState<string>(null);
    const [name_original, setNameOriginal] = useState<string>('')
    const [isActive, setIsActive] = useState<boolean>(film.isActive ?? true)
    const [trailer_url, setTrailerUrl] = useState<string>('')

    const {id} = useParams();

    const youtubeSearchUrl = useMemo(() => {
        const searchTitle = title || name_original || '';
        if (!searchTitle.trim()) {
            return 'https://www.youtube.com/results?search_query=трейлер';
        }
        const query = encodeURIComponent(`трейлер ${searchTitle}`);
        return `https://www.youtube.com/results?search_query=${query}`;
    }, [title, name_original]);

    const updateDataFromImport = (data: any) => {
        const newTitle = data.name ? data.name : '';
        const newNameOriginal = data.name_original || '';

        setTitle(newTitle);
        setDescription(data.description ? data.description : '');
        setYear(data.release_year ? data.release_year : '');
        setNameOriginal(newNameOriginal);

        updateAllOptions(data);
    }

    const handleTrailerUrlChange = (value: string) => {
        const extracted = extractUrlFromHtml(value);
        setTrailerUrl(extracted);
        changeFilm("trailer_url", extracted);
    }

    useEffect(() => {
        if (id) {
            setTitle(film.name);
            setDescription(film.description);
            setNameOriginal(film.name_original);
            setTrailerUrl(film.trailer_url || '');

            if (film.release_year) {
                setYear(film.release_year);
            }

            // Синхронизируем isActive в родительский state, чтобы при сохранении
            // не потерять флаг (иначе isActive отправится как 0)
            changeFilm("isActive", film.isActive ?? true);
        }
    }, []);

    return (
        <div className="film-main-info">
            <div className="form-group">
                <label className="form-group__label">
                    Оригинальное название
                </label>
                <Input
                    value={name_original}
                    onChange={(e) => {
                        setNameOriginal(e.target.value);
                        changeFilm("name_original", e.target.value);
                    }}
                    placeholder="Название"
                />
            </div>

            <div className="form-group">
                <label className="form-group__label">
                    Название <span className="app-required">*</span>
                </label>
                <Input
                    value={title}
                    onChange={(e) => {
                        setTitle(e.target.value);
                        changeFilm("name", e.target.value);
                    }}
                    placeholder="Название"
                />
            </div>

            <div className="form-group">
                <label className="form-group__label">
                    Описание
                </label>
                <TextArea
                    value={description}
                    onChange={(e) => {
                        setDescription(e.target.value);
                        changeFilm("description", e.target.value);
                    }}
                    rows={4}
                    placeholder="Описание"
                />
            </div>

            <div className="form-group">
                <label className="form-group__label">
                    Ссылка на трейлер
                    &#160;
                    (<a
                        href={youtubeSearchUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="film-main-info__youtube-link"
                        title="Найти трейлер на YouTube"
                    >Найти на YouTube
                    </a>)
                </label>
                <Input
                    value={trailer_url}
                    onChange={(e) => handleTrailerUrlChange(e.target.value)}
                    placeholder="https://... или вставьте iframe-код"
                />
            </div>

            <div className="form-group form-group--row">
                <Checkbox
                    id="Active"
                    className="app-unchecked-checkbox app-checkbox-active"
                    checked={isActive}
                    onChange={(event) => {
                        setIsActive(event.target.checked)
                        changeFilm("isActive", event.target.checked);
                    }}
                />
                <label className={"form-group-label"} htmlFor="Active">
                    активный
                </label>
            </div>

            <div className="film-main-info__footer">
                <div className="form-group">
                    <label className="form-group__label" htmlFor={"Year"}>
                        Год выпуска
                    </label>
                    <Select
                        id={"Year"}
                        className={"app-select"}
                        value={year}
                        onChange={(val) => {
                            setYear(val);
                            changeFilm("year", val);
                        }}
                        placeholder="Выберите год"
                        options={generateYearOptions()}
                        style={{width: 300}}
                        allowClear
                    />
                </div>

                <BlockGetInfoById type="film" update={updateDataFromImport} updateOption={updateOption} name_original={name_original} isEdit={!!id} />
            </div>
        </div>
    );
}

export default FilmMainInfo;